import { FacilitatorMessageInput } from '@/components/chat';
import { formatShortDate } from '@/lib/datetime';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';

import { useTeamDetailContext } from './teamDetailContext';

interface ChatMessage {
  id: string;
  content: string;
  senderName: string;
  senderType: 'team_member' | 'facilitator' | 'system';
  channel: 'team' | 'help';
  createdAt: string;
}

// Match the FacilitatorChatPanel chrome (event Chat tab) so the page
// reads as a sibling: rounded card with bordered header, MessageCircle
// icon in the title, full-height flex column body.
const CARD_CLASSES =
  'rounded-xl border bg-card shadow-sm h-full flex flex-col gap-0 py-0';
const CARD_HEADER_CLASSES = 'px-6 py-4 !pb-4 border-b shrink-0';

/**
 * Combined chat view for one team. Shows the team + help channel
 * transcript merged with channel badges, and lets internal staff
 * start or continue a chat by posting to the help channel — same
 * behaviour as the global event Chat tab, scoped to this team.
 *
 * Visually mirrors `FacilitatorChatPanel` (rounded card + bordered
 * header w/ MessageCircle title + sticky input row) so a facilitator
 * moving between event ↔ team pages sees the same shell.
 */
export function EventTeamChatTab() {
  const { eventId, teamId } = useTeamDetailContext();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery<{ success: boolean; data: ChatMessage[] }>({
    queryKey: ['EventTeamChat', eventId, teamId],
    queryFn: () =>
      apiClient.get(`/events/${eventId}/teams/${teamId}/messages?limit=100`),
    enabled: !!eventId && !!teamId,
    // Poll every 5s so player replies appear without a manual refresh
    // — matches the cadence the global FacilitatorChatPanel uses.
    refetchInterval: 5000,
    staleTime: 2000,
  });
  const messages = useMemo(() => data?.data ?? [], [data]);

  // Drop the user at the bottom on load + after each new message so
  // the latest exchange stays in view, same as the event chat panel.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  // Send to the help channel (matches the global Chat tab default —
  // help is the channel facilitator-initiated messages live in).
  // After send we invalidate both the merged transcript and the
  // global "needs response" status so the parent dashboard updates.
  const sendMessageMutation = useMutation({
    mutationFn: async (content: string) =>
      apiClient.post(`/events/${eventId}/teams/${teamId}/messages`, {
        content,
        channel: 'help',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['EventTeamChat', eventId, teamId] });
      queryClient.invalidateQueries({ queryKey: ['TeamsNeedingResponse', eventId] });
      queryClient.invalidateQueries({ queryKey: ['TeamMessages', eventId, teamId] });
    },
  });

  const handleSend = async (content: string) => {
    await sendMessageMutation.mutateAsync(content);
  };

  return (
    <Card className={CARD_CLASSES}>
      <CardHeader className={CARD_HEADER_CLASSES}>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5" />
          Team Chat
        </CardTitle>
        <CardDescription>
          Combined help & team channel transcript — replies post to the help channel
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 !p-0 overflow-hidden flex flex-col">
        <div ref={scrollRef} className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-3/4" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center py-12 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <MessageCircle className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="font-medium text-foreground">No messages yet</p>
              <p className="text-sm text-muted-foreground">
                Send the first message to start a chat with this team.
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {messages.map((m) => (
                <li key={m.id} className="rounded-lg bg-muted/30 p-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{m.senderName}</span>
                      <span
                        className={
                          m.channel === 'help'
                            ? 'rounded bg-warning/10 px-1.5 py-0.5 text-xs text-warning'
                            : 'rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary'
                        }>
                        {m.channel}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatShortDate(m.createdAt, { withTime: true })}
                    </span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                    {m.content}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
        <FacilitatorMessageInput onSend={handleSend} placeholder="Message this team..." />
      </CardContent>
    </Card>
  );
}
